import { pointInPoly, polyArea, polyCentroid } from '../../shared/geom.js';
import { hashUnit } from '../../shared/rng.js';
import type { Area, Building, Canopy, Store } from '../../shared/types.js';
import {
  clearOfTraffic,
  Frame,
  lowestGround,
  orientedBox,
  type ObjectBuilder,
  type ObjectContext,
} from './cityObjects.js';

/**
 * Filling stations and big stores: the two kinds of building a driver navigates by on the
 * edge of a town, and the two the generic extruder drew worst. A forecourt canopy was a
 * four-metre solid block; a supermarket was a terrace of shopfronts standing alone in a car
 * park. Both are drawn here as what they are, from the OSM data and nothing else — the
 * names are OSM's text, in the game's own lettering, and no brand's logo or livery is drawn.
 * A brand's colour is used only where a mapper has put it in `brand:colour`.
 */

// --- canopies ----------------------------------------------------------------

/** A forecourt is lit by its canopy, white and even, much brighter than a street lamp. */
const CANOPY_GLOW = 1.4;
/** Pump islands across the canopy, about one per lane-and-a-half, and along it. */
const ISLAND_PITCH = 7;
const ISLAND_LENGTH = 4.8;
const ISLAND_WIDTH = 1.25;
const ISLAND_KERB = 0.16;
/** The neutral station: white fascia, a dark band, grey pumps. */
const FASCIA = 0xecebe6;
const BAND = 0x2f3a46;
const SOFFIT = 0xe4e3de;
const CANOPY_ROOF = 0xb7b9bb;
const PUMP = 0xdcdcd8;
const COLUMN = 0xb3b7bb;
const KERB = 0xc7c4bc;
const PAD = 0x8f8d87;
const PAD_MARGIN = 3;
/**
 * Over the fills — which a sag in the ground can lift to 13 cm, not just the 10 a square is
 * drawn at — and under the lowest road (14 cm). At 12 cm a lifted fill came up through the
 * concrete; the half centimetre either side is what `onGround` is for.
 */
const PAD_Y = 0.135;
const SCREEN = 0x1c2228;

/** Other roofs on posts: carports, shelters, a market's. Tin, felt or painted steel. */
const SHELTER_ROOFS = [0x8a8d90, 0x6f6a64, 0x7b5d4c, 0x5f6b62, 0x9a9790];
const SHELTER_POST = 0x5d6064;
/** Posts round the edge of a shelter, at most this far apart. */
const POST_SPACING = 6;

export function buildCanopies(out: ObjectBuilder, ctx: ObjectContext): void {
  for (const canopy of ctx.world.objects?.canopies ?? []) {
    const base = lowestGround(canopy.poly, ctx.terrain);
    if (canopy.fuel) buildForecourt(out, ctx, canopy, base);
    else buildShelter(out, ctx, canopy, base);
  }
}

/** The slab itself: a top, an underside, and an edge all round. */
function slab(out: ObjectBuilder, c: Canopy, base: number, roof: number, under: number, edge: number, glow: number, band: number | null): void {
  const top = base + c.top;
  const bottom = base + c.bottom;
  out.paint(roof).polygon(c.poly, top, true);
  out.paint(under, 0, glow).polygon(c.poly, bottom, false);
  const n = c.poly.length / 2;
  const split = band === null ? top : bottom + (top - bottom) * 0.38;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const ax = c.poly[i * 2], az = c.poly[i * 2 + 1];
    const bx = c.poly[j * 2], bz = c.poly[j * 2 + 1];
    const len = Math.hypot(bx - ax, bz - az);
    if (len < 1e-3) continue;
    const facing: [number, number, number] = [(bz - az) / len, 0, -(bx - ax) / len];
    out.paint(edge).quad([ax, split, az, bx, split, bz, bx, top, bz, ax, top, az], facing);
    if (band !== null) out.paint(band).quad([ax, bottom, az, bx, bottom, bz, bx, split, bz, ax, split, az], facing);
  }
}

function buildForecourt(out: ObjectBuilder, ctx: ObjectContext, c: Canopy, base: number): void {
  const accent = c.colour ?? BAND;
  slab(out, c, base, CANOPY_ROOF, SOFFIT, FASCIA, CANOPY_GLOW, accent);

  const box = orientedBox(c.poly);
  if (!box) return;
  // The forecourt's concrete, a little wider than the roof over it. Under every street (it is
  // lower than any of them), so a pad that reaches the road is hidden by the road.
  const pad = new Frame(box.cx, box.cz, box.ux, box.uz, 0);
  const corners = [[-1, -1], [1, -1], [1, 1], [-1, 1]].map(([sa, sb]) => {
    const x = pad.x(sa * (box.halfLength + PAD_MARGIN), sb * (box.halfWidth + PAD_MARGIN));
    const z = pad.z(sa * (box.halfLength + PAD_MARGIN), sb * (box.halfWidth + PAD_MARGIN));
    return [x, ctx.terrain.heightAt(x, z) + PAD_Y, z];
  });
  out.paint(PAD).onGround().quad(corners.flat(), [0, 1, 0]);
  // Islands run along the canopy's long side, which is the way the cars pull through.
  const across = Math.max(1, Math.min(4, Math.round((box.halfWidth * 2) / ISLAND_PITCH)));
  const along = Math.max(1, Math.min(3, Math.floor((box.halfLength * 2) / (ISLAND_LENGTH + 4))));
  const frame = new Frame(box.cx, box.cz, box.ux, box.uz, base);
  for (let i = 0; i < across; i++) {
    const b = -box.halfWidth + ((i + 0.5) * box.halfWidth * 2) / across;
    for (let k = 0; k < along; k++) {
      const a = -box.halfLength + ((k + 0.5) * box.halfLength * 2) / along;
      const x = frame.x(a, b);
      const z = frame.z(a, b);
      // Close to a lane is where an island is: the drive through a forecourt is often mapped
      // as a service road, and the islands stand either side of it.
      if (!pointInPoly(c.poly, x, z) || !clearOfTraffic(ctx, x, z, 0.4)) continue;
      const ground = ctx.terrain.heightAt(x, z) - base;
      const half = ISLAND_LENGTH / 2;
      out.paint(KERB).box(frame, a - half, a + half, b - ISLAND_WIDTH / 2, b + ISLAND_WIDTH / 2, ground - 0.1, ground + ISLAND_KERB);
      const deck = ground + ISLAND_KERB;
      // One column in the middle of the island holds the roof up, as on most canopies.
      out.paint(COLUMN).box(frame, a - 0.2, a + 0.2, b - 0.2, b + 0.2, deck, c.bottom);
      for (const side of [-1, 1]) {
        const p = a + side * 1.35;
        out.paint(PUMP).box(frame, p - 0.42, p + 0.42, b - 0.27, b + 0.27, deck, deck + 1.55);
        out.paint(accent).box(frame, p - 0.44, p + 0.44, b - 0.29, b + 0.29, deck + 1.55, deck + 1.9);
        // The displays, one on each face, lit after dark.
        for (const face of [-1, 1]) {
          const bb = b + face * 0.275;
          out.paint(SCREEN, 0.8, 0.5).quad(
            [...frame.at(p - 0.22, bb, deck + 1.05), ...frame.at(p + 0.22, bb, deck + 1.05), ...frame.at(p + 0.22, bb, deck + 1.4), ...frame.at(p - 0.22, bb, deck + 1.4)],
            [frame.vx * face, 0, frame.vz * face],
          );
        }
      }
    }
  }

  // The forecourt lights, for the street-light map: a light in each quarter of the canopy.
  for (const [sa, sb] of [[-0.5, -0.5], [0.5, -0.5], [0.5, 0.5], [-0.5, 0.5]]) {
    const x = frame.x(sa * box.halfLength, sb * box.halfWidth);
    const z = frame.z(sa * box.halfLength, sb * box.halfWidth);
    if (pointInPoly(c.poly, x, z)) ctx.lights.push({ x, z });
  }

  if (!c.name) return;
  // The name on both long sides of the fascia, where it is read from the road either way.
  const height = Math.min(0.62, (c.top - c.bottom) * 0.55);
  const y = base + c.top - (c.top - c.bottom) * 0.31;
  for (const side of [-1, 1]) {
    const nx = frame.vx * side;
    const nz = frame.vz * side;
    // Out to the fascia: the furthest the ring reaches along this side's normal.
    let reach = -Infinity;
    for (let i = 0; i < c.poly.length; i += 2) {
      reach = Math.max(reach, (c.poly[i] - box.cx) * nx + (c.poly[i + 1] - box.cz) * nz);
    }
    const width = Math.min(box.halfLength * 1.2, 1.2 + c.name.length * 0.42);
    ctx.boards.push({
      text: c.name,
      x: box.cx + nx * (reach + 0.04),
      y,
      z: box.cz + nz * (reach + 0.04),
      // The reader faces -n, so their right is n turned a quarter clockwise seen from above.
      rx: nz,
      rz: -nx,
      width,
      height,
      board: c.colour ?? FASCIA,
      letters: c.colour !== null ? 0xffffff : BAND,
      lit: true,
    });
  }
  totem(out, ctx, c, box);
}

/**
 * The price board on its pole at the forecourt's edge, facing the road: the one thing that
 * tells a driver a hundred metres off that this is a filling station.
 */
function totem(
  out: ObjectBuilder,
  ctx: ObjectContext,
  c: Canopy,
  box: { cx: number; cz: number; ux: number; uz: number; halfLength: number; halfWidth: number },
): void {
  // Out from the middle of each side of the box, to just short of the kerb.
  let best: { x: number; z: number; nx: number; nz: number; d: number } | null = null;
  for (const [nx, nz, half] of [
    [box.ux, box.uz, box.halfLength],
    [-box.ux, -box.uz, box.halfLength],
    [-box.uz, box.ux, box.halfWidth],
    [box.uz, -box.ux, box.halfWidth],
  ]) {
    for (let d = half + 2; d < half + 26; d += 1) {
      const x = box.cx + nx * d;
      const z = box.cz + nz * d;
      const edge = ctx.edges.distance(x, z);
      if (edge > 4) continue;
      // Stepped back to two metres from the kerb, and only if that is clear ground.
      const back = d - Math.max(0, 2 - edge);
      const tx = box.cx + nx * back;
      const tz = box.cz + nz * back;
      if (back > half + 1 && clearOfTraffic(ctx, tx, tz, 1.4) && !ctx.buildings.resolveCircle(tx, tz, 1)) {
        if (!best || back - half < best.d) best = { x: tx, z: tz, nx, nz, d: back - half };
      }
      break;
    }
  }
  if (!best) return;
  const ground = ctx.terrain.heightAt(best.x, best.z) + 0.1;
  const frame = new Frame(best.x, best.z, -best.nz, best.nx, ground);
  const accent = c.colour ?? BAND;
  out.paint(0x6b6f73).box(frame, -0.25, 0.25, -0.15, 0.15, -0.2, 1.2);
  out.paint(accent).box(frame, -0.7, 0.7, -0.18, 0.18, 1.2, 6.2);
  // Four rows of prices, dark by day and lit at night.
  for (const face of [-1, 1]) {
    for (let row = 0; row < 4; row++) {
      const h = 2.0 + row * 0.62;
      out.paint(0x121518, 0.6, 0.9).quad(
        [...frame.at(-0.55, face * 0.185, h), ...frame.at(0.55, face * 0.185, h), ...frame.at(0.55, face * 0.185, h + 0.42), ...frame.at(-0.55, face * 0.185, h + 0.42)],
        [frame.vx * face, 0, frame.vz * face],
      );
    }
    ctx.boards.push({
      text: c.name!,
      x: frame.x(0, face * 0.2),
      y: ground + 5.45,
      z: frame.z(0, face * 0.2),
      // The frame's u is the reader's right on the face that looks back at the canopy.
      rx: frame.ux * face,
      rz: frame.uz * face,
      width: 1.24,
      height: 0.42,
      board: c.colour ?? FASCIA,
      letters: c.colour !== null ? 0xffffff : BAND,
      lit: true,
    });
  }
}

function buildShelter(out: ObjectBuilder, ctx: ObjectContext, c: Canopy, base: number): void {
  const look = hashUnit(ctx.world.seed, c.id ^ 0x51ed27);
  const roof = c.colour ?? SHELTER_ROOFS[Math.floor(look * SHELTER_ROOFS.length) % SHELTER_ROOFS.length];
  slab(out, c, base, roof, 0x6c6c6a, roof, 0, null);
  // Posts at the corners, a little in, and along any side long enough to need them.
  const centre = polyCentroid(c.poly);
  const n = c.poly.length / 2;
  if (n > 24) return;
  out.paint(SHELTER_POST);
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const ax = c.poly[i * 2], az = c.poly[i * 2 + 1];
    const bx = c.poly[j * 2], bz = c.poly[j * 2 + 1];
    const len = Math.hypot(bx - ax, bz - az);
    const steps = Math.max(1, Math.ceil(len / POST_SPACING));
    for (let k = 0; k < steps; k++) {
      const t = k / steps;
      let x = ax + (bx - ax) * t;
      let z = az + (bz - az) * t;
      const dx = centre.x - x;
      const dz = centre.z - z;
      const d = Math.hypot(dx, dz);
      if (d < 0.6) continue;
      x += (dx / d) * 0.3;
      z += (dz / d) * 0.3;
      if (!clearOfTraffic(ctx, x, z, 0.3)) continue;
      const g = ctx.terrain.heightAt(x, z);
      out.beam(x, g, z, x, base + c.bottom, z, 0.14);
    }
  }
}

// --- stores ------------------------------------------------------------------

const GLASS = 0x2a3640;
const MULLION = 0x3a3f44;
const ENTRANCE_CANOPY = 0xd9d8d3;
const STORE_BOARD = 0x23292f;

interface Wall {
  ax: number;
  az: number;
  bx: number;
  bz: number;
  /** Unit tangent a → b, and the outward normal. */
  tx: number;
  tz: number;
  nx: number;
  nz: number;
  len: number;
}

export function buildStores(out: ObjectBuilder, ctx: ObjectContext): void {
  const stores = ctx.world.objects?.stores ?? [];
  if (!stores.length) return;
  const byId = new Map(ctx.world.buildings.map((b) => [b.id, b]));
  const lots = ctx.world.areas.filter((a) => a.kind === 'parking');
  const forecourts = (ctx.world.objects?.canopies ?? []).filter((c) => c.fuel).map((c) => polyCentroid(c.poly));
  for (const store of stores) {
    const b = byId.get(store.building);
    if (!b) continue;
    const walls = wallsOf(b, store.kind === 'kiosk' ? 3 : 7);
    if (!walls.length) continue;
    const scored = walls
      .map((w) => ({ w, score: entranceScore(ctx, w, lots, store.kind === 'kiosk' ? forecourts : []) }))
      .sort((p, q) => q.score - p.score);
    const base = lowestGround(b.poly, ctx.terrain) + b.minHeight;
    const look = hashUnit(ctx.world.seed, b.id ^ 0x3c6ef372);
    entrance(out, ctx, store, b, scored[0].w, base, look);
    if (store.kind === 'mall') {
      // A shopping centre has a way in from the street and another from its car park.
      const second = scored.find((s, i) => i > 0 && s.score > 0.5 && s.w.nx * scored[0].w.nx + s.w.nz * scored[0].w.nz < 0.3);
      if (second) entrance(out, ctx, store, b, second.w, base, (look * 7.1) % 1);
    } else if (store.kind === 'supermarket' && store.name) {
      // And its name again on the side the street sees, if that is not the entrance's.
      const street = walls
        .filter((w) => w !== scored[0].w && w.len >= 10)
        .map((w) => ({ w, d: ctx.edges.distance((w.ax + w.bx) / 2 + w.nx * 3, (w.az + w.bz) / 2 + w.nz * 3) }))
        .sort((p, q) => p.d - q.d)[0];
      if (street && street.d < 20) {
        nameBoard(ctx, store, street.w, street.w.len / 2, Math.min(base + b.height - b.minHeight - 0.9, base + 5.2), 1.1);
      }
    }
  }
}

function wallsOf(b: Building, minLength: number): Wall[] {
  const out: Wall[] = [];
  const n = b.poly.length / 2;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const ax = b.poly[i * 2], az = b.poly[i * 2 + 1];
    const bx = b.poly[j * 2], bz = b.poly[j * 2 + 1];
    const len = Math.hypot(bx - ax, bz - az);
    if (len < minLength) continue;
    const tx = (bx - ax) / len;
    const tz = (bz - az) / len;
    out.push({ ax, az, bx, bz, tx, tz, nx: tz, nz: -tx, len });
  }
  return out;
}

/**
 * Which wall the door is in. OSM's `entrance=*` nodes would say, and are not fetched: there
 * is one on nearly every block of flats in Poland, and the query would double for the sake
 * of a few dozen shops. So the door faces the car park, where there is one in front of a
 * wall; else the street; and never a wall with another building against it.
 */
function entranceScore(ctx: ObjectContext, w: Wall, lots: Area[], forecourts: { x: number; z: number }[]): number {
  const mx = (w.ax + w.bx) / 2;
  const mz = (w.az + w.bz) / 2;
  let score = Math.min(w.len, 40) / 80;
  for (const d of [6, 14]) {
    const x = mx + w.nx * d;
    const z = mz + w.nz * d;
    if (lots.some((a) => pointInPoly(a.poly, x, z))) score += 1;
  }
  const road = ctx.edges.distance(mx + w.nx * 3, mz + w.nz * 3);
  if (road < 25) score += 0.8 * (1 - road / 25);
  for (const f of forecourts) {
    const dx = f.x - mx;
    const dz = f.z - mz;
    if (dx * w.nx + dz * w.nz > 0 && Math.hypot(dx, dz) < 35) score += 2;
  }
  if (ctx.buildings.resolveCircle(mx + w.nx * 1.5, mz + w.nz * 1.5, 0.6)) score -= 5;
  return score;
}

function entrance(
  out: ObjectBuilder,
  ctx: ObjectContext,
  store: Store,
  b: Building,
  w: Wall,
  base: number,
  look: number,
): void {
  const tall = b.height - b.minHeight;
  const kiosk = store.kind === 'kiosk';
  const mall = store.kind === 'mall';
  const width = Math.min(w.len * (kiosk ? 0.7 : mall ? 0.32 : 0.42), kiosk ? 9 : mall ? 14 : 12);
  const glassTop = Math.min(tall - 0.6, kiosk ? 2.7 : mall ? 6 : 3.4);
  if (width < 2 || glassTop < 2) return;
  // A supermarket's door is usually at one end of its glazing, near a corner; a centre's in the middle.
  const at = mall || kiosk ? w.len / 2 : w.len * (look < 0.5 ? 0.3 : 0.7);
  const s0 = Math.max(0.4, Math.min(w.len - width - 0.4, at - width / 2));
  const s1 = s0 + width;
  const frame = new Frame(w.ax, w.az, w.tx, w.tz, base);
  // The frame's v is the wall's inward side; out is -v. Far enough out that the depth buffer
  // can tell the glass from the wall it is on across the city: at 5 cm, a supermarket's front
  // flickered between glazing and the generic facade behind it from a few hundred metres.
  const off = -0.15;
  out.paint(GLASS, 1, 0.55).quad(
    [...frame.at(s0, off, 0.05), ...frame.at(s1, off, 0.05), ...frame.at(s1, off, glassTop), ...frame.at(s0, off, glassTop)],
    [w.nx, 0, w.nz],
  );
  // Mullions every metre and a half, and a transom over the doors.
  out.paint(MULLION);
  const bays = Math.max(1, Math.round(width / 1.5));
  for (let k = 0; k <= bays; k++) {
    const s = s0 + (k * width) / bays;
    out.box(frame, s - 0.05, s + 0.05, off - 0.06, off, 0, glassTop);
  }
  out.box(frame, s0, s1, off - 0.06, off, glassTop - 0.08, glassTop + 0.04);
  out.box(frame, s0, s1, off - 0.06, off, 2.15, 2.25);

  // The canopy over the way in, on two posts at its outer corners.
  const depth = kiosk ? 1.4 : mall ? 3.2 : 2.6;
  const roof = store.colour ?? ENTRANCE_CANOPY;
  const c0 = s0 - 0.6;
  const c1 = s1 + 0.6;
  const y = glassTop + 0.1;
  out.paint(roof).box(frame, c0, c1, -depth, 0, y, y + 0.32, true);
  if (!kiosk) {
    for (const s of [c0 + 0.2, c1 - 0.2]) {
      const x = frame.x(s, -depth + 0.25);
      const z = frame.z(s, -depth + 0.25);
      if (!clearOfTraffic(ctx, x, z, 0.5)) continue;
      out.paint(MULLION).beam(x, ctx.terrain.heightAt(x, z), z, x, base + y, z, 0.16);
    }
  }
  if (store.name) {
    // Over the canopy where the wall is tall enough, else along the canopy's own edge.
    const boardHeight = kiosk ? 0.6 : mall ? 1.5 : 1.25;
    const above = y + 0.32 + 0.3 + boardHeight / 2;
    if (above + boardHeight / 2 + 0.2 <= tall) nameBoard(ctx, store, w, (s0 + s1) / 2, base + above, boardHeight);
    else nameBoard(ctx, store, w, (s0 + s1) / 2, base + y + 0.16, 0.28, depth + 0.03);
  }
}

function nameBoard(ctx: ObjectContext, store: Store, w: Wall, at: number, y: number, height: number, out = 0.12): void {
  const name = store.name!;
  const width = Math.min(w.len * 0.8, height * (1 + name.length * 0.62));
  ctx.boards.push({
    text: name,
    x: w.ax + w.tx * at + w.nx * out,
    y,
    z: w.az + w.tz * at + w.nz * out,
    rx: -w.tx,
    rz: -w.tz,
    width,
    height,
    board: store.colour ?? STORE_BOARD,
    letters: 0xf4f2ec,
    lit: true,
  });
}

/** For the tests: the area a canopy's slab covers. */
export function canopyArea(c: Canopy): number {
  return Math.abs(polyArea(c.poly));
}
